import React, { useEffect, useState } from 'react';

/** @jsxRuntime classic */
/** @jsx jsxCustomEvent */
import jsxCustomEvent from '@micro-zoe/micro-app/polyfill/jsx-custom-event';

import { useLocalStorageState, useTitle } from 'ahooks';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import microApp from '@micro-zoe/micro-app';
import { AppEnv } from '@/types/enum';

import { ALL_APP_CONFIGS } from './config';
import Logo from '@/components/layout/logo';
import { formatURLParams, isUrlEncoded } from '@/utils';
import { Watermark } from 'antd';
import { useUserInfo } from '@joyu/auth';
import dayjs from 'dayjs';
import useStore from '@/hooks/use-store';
import Header from '@/components/layout/header';
import { useTranslation } from 'react-i18next';

const getWatermarkContent = (
  userInfo: { username?: string; email?: string }
): string[] => {
  const lines: string[] = [`${userInfo.username ?? ''} ${userInfo.email ?? ''}`];
  lines.push(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  return lines;
};

const SubApp: React.FC = () => {
  const { i18n } = useTranslation();
  const { appKey } = useParams<{ appKey: string }>();
  const {
    headerStyle,
    setSubAppAvatarFunctions,
    currentSubAppKey,
    setCurrentSubAppKey,
    setHeaderStyle,
  } = useStore();

  useEffect(() => {
    if (appKey) {
      const subSearch = window.location.search.split(`${appKey}=`)[1];
      if (subSearch && !isUrlEncoded(subSearch)) {
        window.location.href = `/app/${appKey}?${appKey}=${encodeURIComponent(
          decodeURIComponent(subSearch)
        )}`;
      }
    }
  }, [appKey]);
  const navigate = useNavigate();
  // useEffect(() => {
  //   if (window.location.search.includes('login')) {
  //     navigate('/login');
  //   }
  // }, [navigate]);

  // 如果没有登录，跳转登录页
  const [token] = useLocalStorageState(`JOY_ADMIN_${import.meta.env.VITE_APP_ENV}_TOKEN`, {
    defaultValue: '',
  });
  useEffect(() => {
    if (!token && window.location.href.indexOf('login') < 0) {
      navigate(`/login?redirect=${window.location.pathname}${window.location.search}`);
    }
  }, [navigate, token]);

  const { mainUserInfo } = useUserInfo();
  const [showLogo, setShowLogo] = useState(true);
  const [, setToken] = useLocalStorageState<string>(
    `JOY_ADMIN_${import.meta.env.VITE_APP_ENV}_TOKEN`,
    {
      defaultValue: '',
    }
  );
  const [, setAdminInfo] = useLocalStorageState(
    `JOY_ADMIN_${import.meta.env.VITE_APP_ENV}_USER_INFO`,
    {
      defaultValue: '',
    }
  );
  useTitle(
    ALL_APP_CONFIGS[appKey]?.envName?.[import.meta.env.VITE_APP_ENV as AppEnv] ||
      ALL_APP_CONFIGS[appKey]?.name ||
      '欢乐互娱研发工具平台'
  );

  useEffect(() => {
    const lastProjectId = sessionStorage.getItem('__last_favicon_project_id__');
    const key = lastProjectId ? `__favicon_url__:${lastProjectId}` : '__favicon_url__';
    const cached = sessionStorage.getItem(key);
    if (cached) {
      document
        .querySelectorAll("link[rel*='icon']")
        .forEach((el) => el.parentNode?.removeChild(el));
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = `${cached}?_t=${Date.now()}`;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!currentSubAppKey || currentSubAppKey !== ALL_APP_CONFIGS[appKey].key) {
      setHeaderStyle(ALL_APP_CONFIGS[appKey].header);
      setCurrentSubAppKey(ALL_APP_CONFIGS[appKey].key);
    }
  }, [appKey, currentSubAppKey, setCurrentSubAppKey, setHeaderStyle]);

  useEffect(() => {
    if (currentSubAppKey) {
      microApp.setData(currentSubAppKey, {
        event: 'change-language',
        data: i18n.language,
      });
    }
  }, [i18n, currentSubAppKey]);

  if (!appKey || !ALL_APP_CONFIGS[appKey]) {
    return <Navigate to="/" />;
  }
  return (
    <Watermark
      font={{
        color: '#efefef66',
        fontSize: 24,
      }}
      content={getWatermarkContent(mainUserInfo)}
    >
      {headerStyle === 'system' ? <Header /> : showLogo && <Logo fixed theme="light" />}
      <micro-app
        style={{ width: '100vw', height: 'calc(100vh - )' }}
        name={ALL_APP_CONFIGS[appKey].key}
        url={ALL_APP_CONFIGS[appKey].url[import.meta.env.VITE_APP_ENV as AppEnv]}
        onDataChange={(e: any) => {
          const { data } = e.detail;
          switch (data.event) {
            case 'hide-logo':
              setShowLogo(false);
              break;
            case 'show-logo':
              setShowLogo(true);
              break;
            case 'log-out':
              setToken('');
              setAdminInfo('');
              navigate('/login');
              break;
            case 'navigate':
              navigate(data.params.path);
              break;
            case 'switch-app':
              const { appCode, path, params, target, projectCode } = data.params;
              const url = `/app/${appCode}?${appCode}=${encodeURIComponent(
                path + (params || projectCode ? '?' + formatURLParams(params, projectCode) : '')
              )}`;
              switch (target) {
                case '_blank':
                  window.open(url);
                  break;
                case '_self':
                default:
                  navigate(url);
                  break;
              }
              break;
            case 'set-favicon':
              const faviconUrl = data.params?.url as string | undefined;
              const faviconProjectId = data.params?.projectId;
              if (faviconUrl) {
                const faviconKey = faviconProjectId
                  ? `__favicon_url__:${faviconProjectId}`
                  : '__favicon_url__';
                sessionStorage.setItem(faviconKey, faviconUrl);
                if (faviconProjectId) {
                  sessionStorage.setItem('__last_favicon_project_id__', String(faviconProjectId));
                }
                document
                  .querySelectorAll("link[rel*='icon']")
                  .forEach((el) => el.parentNode?.removeChild(el));
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = `${faviconUrl}?_t=${Date.now()}`;
                document.head.appendChild(link);
              }
              break;
            case 'set-avatar-functions':
              setSubAppAvatarFunctions(data.params.functions || []);
              break;
            default:
              break;
          }
        }}
      />
    </Watermark>
  );
};

export default React.memo(SubApp);
